import * as React from 'react';
import * as searchBarStyles from './search-bar.module.scss';
import { navigate, withPrefix } from 'gatsby';
import { useLocation } from '../../hooks/location';
import SearchBarIcon from '../icons/search-bar-icon';
import SearchBarAiIcon from '../icons/search-bar-ai-icon';
import ClearIcon from '../icons/clear-icon';
import ThemeSwitcher from '../theme-switcher/theme-switcher';
import MobileMenuSwitch from '../mobile-menu-switch/mobile-menu-switch';
import { usePageData } from '../../hooks/page-data';
import { useSiteMetadata } from '../../queries/site-medatata';
import { AiButton } from '../ai-button/ai-button';
import { uri } from '../../utilities/uri';

const SearchBar = ({ topPx }) => {
    const location = useLocation();
    const [searchText, setSearchText] = React.useState('');
    const searchInputRef = React.useRef(null);
    const siteMetadata = useSiteMetadata();
    const searchProvider = (siteMetadata?.searchProvider).toLowerCase();

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const searchTextParam = params.get('q');

        let searchText = '';
        if (searchTextParam) {
            searchText = searchTextParam;
        }

        setSearchText(searchText);
    }, [location]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (searchText === '') {
            return;
        }

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('q', searchText);

        navigate(`/search?${searchParams}`);
    }

    //// Set and remove document scroll-padding-top style, because when typing in input the page scrolling to top (bug with Chrome).
    const onFocus = () => {
        document.documentElement.classList.add(`searchBarFocused`);
    }

    const onBlur = () => {
        document.documentElement.classList.remove(`searchBarFocused`);
    }

    React.useEffect(() => {
        return () => onBlur();
    }, []);

    const data = usePageData();
    const fullscreen = React.useMemo(() => data?.markdownRemark?.frontmatter?.fullscreen || false, [data]);
    const landingPage = React.useMemo(() => data?.markdownRemark?.frontmatter?.landing_page || false, [data]);

    const clearInputField = (e) => {
        onFocus();
        e.stopPropagation();
        setSearchText('');
        searchInputRef.current?.focus();
    };

    const navigateToAiChat = () => {
        const url = searchText ? uri`/search/ai-chat?q=${searchText}` : '/search/ai-chat';
        navigate(url);
    }

    return (
        <div className={`${searchBarStyles.container} ${fullscreen && !landingPage ? searchBarStyles.fullscreen : ''} ${landingPage ? searchBarStyles.landingPage : ''} d-print-none`} style={{ top: `${topPx}px` }} >
            <div className={searchBarStyles.mobileSwitchContainer}>
                <MobileMenuSwitch />
            </div>
            <div className={searchBarStyles.searchContainer} >
                <form method='GET' action={withPrefix('/search')} onSubmit={(e) => handleSubmit(e)}>
                    <div className={searchBarStyles.formContainer}>
                        <div className={searchBarStyles.iconContainer}>
                            {searchProvider === 'google' ?
                                <SearchBarIcon className={`${searchBarStyles.searchIcon} ${searchText !== '' ? searchBarStyles.searchIconColor : ''}`} />
                                :
                                <SearchBarAiIcon className={`${searchBarStyles.searchIcon} ${searchText !== '' ? searchBarStyles.searchIconColor : ''}`} />
                            }
                        </div>
                        <input ref={searchInputRef} className={searchBarStyles.searchBar} value={searchText}
                            onChange={(e) => setSearchText(e.target.value)} autoComplete="off" type="text" placeholder="Search..." name="q"
                            onBlur={onBlur} onFocus={onFocus} />
                        <div className={searchBarStyles.buttonsContainer}>
                            {
                                searchText !== '' &&
                                <button type="button" className={`${searchBarStyles.cancelButton}`} onClick={clearInputField} aria-label='Clear'>
                                    <ClearIcon />
                                </button>
                            }
                            {
                                searchProvider !== 'google' && <AiButton onClick={navigateToAiChat} contentClassName='d-none d-sm-block'>Ask AI</AiButton>
                            }
                        </div>
                    </div>
                </form>
            </div>
            <div className={searchBarStyles.themeSwitchContainer}>
                <ThemeSwitcher />
            </div>
        </div>
    )
}

export default SearchBar;